import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/pl";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
    useCreateIdentificationDocument,
    useDeleteIdentificationDocument,
    useIdentificationDocuments,
} from "../../../services/customersService";
import BasicDialog from "../../base/BasicDialog";
import { SnackbarError } from "../../base/SnackbarAlert/SnackbarError";
import { SnackbarSuccess } from "../../base/SnackbarAlert/SnackbarSuccess";

const DOCUMENT_TYPES = [
    { value: "passport", label: "clients.IdentificationDocumentsDialog.documentTypes.passport" },
    { value: "id_card", label: "clients.IdentificationDocumentsDialog.documentTypes.idCard" },
    {
        value: "drivers_license",
        label: "clients.IdentificationDocumentsDialog.documentTypes.driversLicense",
    },
    {
        value: "residence_permit",
        label: "clients.IdentificationDocumentsDialog.documentTypes.residencePermit",
    },
    {
        value: "m_card",
        label: "clients.IdentificationDocumentsDialog.documentTypes.mCard",
    },
    { value: "other", label: "clients.IdentificationDocumentsDialog.documentTypes.other" },
];

const MAX_DOCUMENTS = 6;

export const IdentificationDocumentsDialog = (props) => {
    const { open, onClose, personId } = props;
    const { t } = useTranslation();

    dayjs.locale("pl");

    const { data: documents = [], isLoading, refetch } = useIdentificationDocuments(personId);

    const [newDocument, setNewDocument] = useState({
        document_type: "id_card",
        number: "",
        expires_at: "",
        issuing_authority: "",
    });

    const [numberError, setNumberError] = useState("");

    const { mutate: createDocument, isPending: isCreating } = useCreateIdentificationDocument({
        onSuccess: () => {
            toast.custom(() => (
                <SnackbarSuccess
                    message={t("clients.IdentificationDocumentsDialog.createSuccess")}
                />
            ));
            setNewDocument({
                document_type: "id_card",
                number: "",
                expires_at: "",
                issuing_authority: "",
            });
            refetch();
        },
        onError: (error) => {
            console.error("Error creating document:", error);
            toast.custom(() => (
                <SnackbarError message={t("clients.IdentificationDocumentsDialog.createError")} />
            ));
        },
    });

    const { mutate: deleteDocument } = useDeleteIdentificationDocument({
        onSuccess: () => {
            toast.custom(() => (
                <SnackbarSuccess
                    message={t("clients.IdentificationDocumentsDialog.deleteSuccess")}
                />
            ));
            refetch();
        },
        onError: (error) => {
            console.error("Error deleting document:", error);
            toast.custom(() => (
                <SnackbarError message={t("clients.IdentificationDocumentsDialog.deleteError")} />
            ));
        },
    });

    const validateDocumentNumber = (type, number) => {
        if (!number.trim()) {
            return "";
        }

        if (type === "id_card") {
            const idCardRegex = /^[A-Z]{3}\d{6}$/;
            if (!idCardRegex.test(number)) {
                return t("clients.IdentificationDocumentsDialog.invalidIdCardFormat");
            }
        } else if (type === "passport") {
            const passportRegex = /^[A-Z]{2}\d{6,7}$/;
            if (!passportRegex.test(number)) {
                return t("clients.IdentificationDocumentsDialog.invalidPassportFormat");
            }
        }

        return "";
    };

    const handleNumberChange = (value) => {
        const upperValue = value.toUpperCase();
        setNewDocument({
            ...newDocument,
            number: upperValue,
        });
        setNumberError(validateDocumentNumber(newDocument.document_type, upperValue));
    };

    const handleDocumentTypeChange = (type) => {
        setNewDocument({
            ...newDocument,
            document_type: type,
        });
        setNumberError(validateDocumentNumber(type, newDocument.number));
    };

    const handleCreateDocument = () => {
        if (!newDocument.number.trim()) {
            toast.custom(() => (
                <SnackbarError
                    message={t("clients.IdentificationDocumentsDialog.numberRequired")}
                />
            ));
            return;
        }

        const validationError = validateDocumentNumber(
            newDocument.document_type,
            newDocument.number,
        );
        if (validationError) {
            setNumberError(validationError);
            return;
        }

        if (!newDocument.expires_at) {
            toast.custom(() => (
                <SnackbarError
                    message={t("clients.IdentificationDocumentsDialog.expiryDateRequired")}
                />
            ));
            return;
        }

        // const expiryDate = new Date(newDocument.expires_at);
        // const today = new Date();
        // today.setHours(0, 0, 0, 0);

        // if (expiryDate < today) {
        //     toast.custom(() => (
        //         <SnackbarError
        //             message={t("clients.IdentificationDocumentsDialog.expiryDatePast")}
        //         />
        //     ));
        //     return;
        // }

        const formattedData = {
            document_type: newDocument.document_type,
            number: newDocument.number,
            expires_at: newDocument.expires_at,
            issuing_authority: newDocument.issuing_authority.trim() || "",
        };

        createDocument({ personId, data: formattedData });
    };

    const handleDeleteDocument = (documentId) => {
        if (window.confirm(t("clients.IdentificationDocumentsDialog.confirmDelete"))) {
            deleteDocument({ personId, documentId });
        }
    };

    const canAddMore = documents.length < MAX_DOCUMENTS;

    // odfiltrowujemy typy które już są (poza other)
    const existingDocumentTypes = documents
        .map((doc) => doc.document_type)
        .filter((type) => type !== "other");
    const availableDocumentTypes = DOCUMENT_TYPES.filter(
        (type) => type.value === "other" || !existingDocumentTypes.includes(type.value),
    );

    useEffect(() => {
        if (availableDocumentTypes.length > 0) {
            const firstAvailableType = availableDocumentTypes[0].value;
            if (!availableDocumentTypes.find((t) => t.value === newDocument.document_type)) {
                setNewDocument((prev) => ({
                    ...prev,
                    document_type: firstAvailableType,
                }));
                setNumberError("");
            }
        }
    }, [availableDocumentTypes, newDocument.document_type]);

    if (isLoading) {
        return (
            <BasicDialog
                open={open}
                onClose={onClose}
                title={t("clients.IdentificationDocumentsDialog.title")}
                maxWidth="lg"
                minWidth={900}
                showDialogActions={false}
            >
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            </BasicDialog>
        );
    }

    return (
        <BasicDialog
            open={open}
            onClose={onClose}
            title={t("clients.IdentificationDocumentsDialog.title")}
            maxWidth="lg"
            minWidth={1000}
            showDialogActions={false}
        >
            <Box sx={{ minWidth: 700 }}>
                {documents.length >= MAX_DOCUMENTS && (
                    <Typography
                        color="warning.main"
                        sx={{ mb: 2, fontSize: "1.2rem", fontWeight: 500 }}
                    >
                        {t("clients.IdentificationDocumentsDialog.maxDocumentsReached", {
                            max: MAX_DOCUMENTS,
                        })}
                    </Typography>
                )}
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pl">
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow
                                    sx={{
                                        backgroundColor: (theme) => theme.palette.primary.main,
                                        height: 56,
                                    }}
                                >
                                    <TableCell
                                        sx={{
                                            width: 220,
                                            minWidth: 220,
                                            color: (theme) => theme.palette.primary.contrastText,
                                            fontSize: "1.15rem",
                                            fontWeight: 500,
                                        }}
                                    >
                                        {t("clients.IdentificationDocumentsDialog.documentType")}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            width: 200,
                                            minWidth: 200,
                                            color: (theme) => theme.palette.primary.contrastText,
                                            fontSize: "1.15rem",
                                            fontWeight: 500,
                                        }}
                                    >
                                        {t("clients.IdentificationDocumentsDialog.number")}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            width: 180,
                                            minWidth: 180,
                                            color: (theme) => theme.palette.primary.contrastText,
                                            fontSize: "1.15rem",
                                            fontWeight: 500,
                                        }}
                                    >
                                        {t("clients.IdentificationDocumentsDialog.expiryDate")}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            width: 220,
                                            minWidth: 220,
                                            color: (theme) => theme.palette.primary.contrastText,
                                            fontSize: "1.15rem",
                                            fontWeight: 500,
                                        }}
                                    >
                                        {t(
                                            "clients.IdentificationDocumentsDialog.issuingAuthority",
                                        )}
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            width: 100,
                                            minWidth: 100,
                                            color: (theme) => theme.palette.primary.contrastText,
                                            fontSize: "1.15rem",
                                            fontWeight: 500,
                                        }}
                                    >
                                        {t("clients.IdentificationDocumentsDialog.actions")}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {documents.map((doc) => (
                                    <TableRow key={doc.id} sx={{ height: 48 }}>
                                        <TableCell
                                            sx={{
                                                width: 220,
                                                minWidth: 220,
                                                fontSize: "1.15rem",
                                                fontWeight: 400,
                                                py: 2,
                                            }}
                                        >
                                            {t(
                                                DOCUMENT_TYPES.find(
                                                    (dt) => dt.value === doc.document_type,
                                                )?.label || doc.document_type,
                                            )}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                width: 200,
                                                minWidth: 200,
                                                fontSize: "1.15rem",
                                                fontWeight: 400,
                                                py: 2,
                                            }}
                                        >
                                            {doc.number}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                width: 180,
                                                minWidth: 180,
                                                fontSize: "1.15rem",
                                                fontWeight: 400,
                                                py: 2,
                                            }}
                                        >
                                            {doc.expires_at
                                                ? new Date(doc.expires_at).toLocaleDateString()
                                                : "–"}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                width: 220,
                                                minWidth: 220,
                                                fontSize: "1.15rem",
                                                fontWeight: 400,
                                                py: 2,
                                            }}
                                        >
                                            {doc.issuing_authority || "–"}
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                width: 100,
                                                minWidth: 100,
                                                fontSize: "1.15rem",
                                                fontWeight: 400,
                                                py: 2,
                                            }}
                                        >
                                            <IconButton
                                                color="error"
                                                size="small"
                                                onClick={() => handleDeleteDocument(doc.id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {canAddMore && availableDocumentTypes.length > 0 && (
                                    <TableRow sx={{ height: 48 }}>
                                        <TableCell
                                            sx={{
                                                width: 220,
                                                minWidth: 220,
                                                fontSize: "1.15rem",
                                                fontWeight: 400,
                                                py: 2,
                                            }}
                                        >
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    value={newDocument.document_type}
                                                    onChange={(e) =>
                                                        handleDocumentTypeChange(e.target.value)
                                                    }
                                                    sx={{ fontSize: "1.15rem" }}
                                                >
                                                    {availableDocumentTypes.map((type) => (
                                                        <MenuItem
                                                            key={type.value}
                                                            value={type.value}
                                                            sx={{ fontSize: "1.15rem" }}
                                                        >
                                                            {t(type.label)}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                width: 200,
                                                minWidth: 200,
                                                fontSize: "1.15rem",
                                                fontWeight: 400,
                                                py: 2,
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                size="small"
                                                required
                                                error={!!numberError}
                                                value={newDocument.number}
                                                onChange={(e) => handleNumberChange(e.target.value)}
                                                placeholder={t(
                                                    "clients.IdentificationDocumentsDialog.numberPlaceholder",
                                                )}
                                                inputProps={{ style: { fontSize: "1.15rem" } }}
                                            />
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                width: 180,
                                                minWidth: 180,
                                                fontSize: "1.15rem",
                                                fontWeight: 400,
                                                py: 2,
                                            }}
                                        >
                                            <DatePicker
                                                value={
                                                    newDocument.expires_at
                                                        ? dayjs(newDocument.expires_at)
                                                        : null
                                                }
                                                onChange={(date) => {
                                                    setNewDocument({
                                                        ...newDocument,
                                                        expires_at: date
                                                            ? date.format("YYYY-MM-DD")
                                                            : "",
                                                    });
                                                }}
                                                // minDate={dayjs()}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        fullWidth
                                                        size="small"
                                                        required
                                                        inputProps={{
                                                            ...params.inputProps,
                                                            style: { fontSize: "1.15rem" },
                                                        }}
                                                    />
                                                )}
                                            />
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                width: 220,
                                                minWidth: 220,
                                                fontSize: "1.15rem",
                                                fontWeight: 400,
                                                py: 2,
                                            }}
                                        >
                                            {newDocument.document_type !== 'm_card' && <TextField
                                                fullWidth
                                                size="small"
                                                value={newDocument.issuing_authority}
                                                onChange={(e) =>
                                                    setNewDocument({
                                                        ...newDocument,
                                                        issuing_authority: e.target.value,
                                                    })
                                                }
                                                placeholder={t(
                                                    "clients.IdentificationDocumentsDialog.authorityPlaceholder",
                                                )}
                                                inputProps={{ style: { fontSize: "1.15rem" } }}
                                            />}
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                width: 100,
                                                minWidth: 100,
                                                fontSize: "1.15rem",
                                                fontWeight: 400,
                                                py: 2,
                                            }}
                                        >
                                            <IconButton
                                                sx={{
                                                    color: (theme) => theme.palette.success.main,
                                                }}
                                                size="small"
                                                onClick={handleCreateDocument}
                                                disabled={
                                                    isCreating ||
                                                    !newDocument.number.trim() ||
                                                    !newDocument.expires_at ||
                                                    !!numberError
                                                }
                                            >
                                                {isCreating ? (
                                                    <CircularProgress size={20} />
                                                ) : (
                                                    <AddIcon />
                                                )}
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </LocalizationProvider>
                <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
                    <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        onClick={onClose}
                        color="primary"
                    >
                        {t("clients.IdentificationDocumentsDialog.close")}
                    </Button>
                </Box>
            </Box>
        </BasicDialog>
    );
};
